import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Users, UserPlus, X, Check, Loader2, Search } from "lucide-react";

const Departments = () => {
    const { users, depts, ready, refreshUsers } = useData();
    const { user: currentUser } = useAuth();
    const isSuperAdmin = currentUser?.role === "super_admin";
    const [editing, setEditing] = useState(null); // dept object being edited

    const countByDept = (id) => users.filter((u) => u.department_id === id).length;
    const homeManagersByDept = (id) => users.filter((u) => u.department_id === id && (u.role === "manager" || u.role === "super_admin"));
    const additionalManagersByDept = (id) => users.filter((u) => Array.isArray(u.managed_department_ids) && u.managed_department_ids.includes(id));

    return (
        <div className="space-y-6" data-testid="departments-page">
            <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Organization</div>
                <h1 className="font-heading text-4xl font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-3">
                    <Building2 className="h-7 w-7 text-[#2EB4AC]" />
                    Departments
                </h1>
                <p className="text-slate-500 mt-1">All organizational units — add extra managers to give them cross-department scope.</p>
            </div>

            {!ready ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[0,1,2,3,4,5].map((i) => (
                        <div key={i} className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 animate-pulse">
                            <div className="flex items-start justify-between">
                                <div className="h-10 w-10 bg-slate-100 rounded-lg" />
                                <div className="h-5 w-16 bg-slate-100 rounded-full" />
                            </div>
                            <div className="h-5 w-32 bg-slate-100 rounded mt-4" />
                            <div className="h-3 w-48 bg-slate-100 rounded mt-2" />
                        </div>
                    ))}
                </div>
            ) : depts.length === 0 ? (
                <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-10 text-center">
                    <Building2 className="h-8 w-8 text-[#2EB4AC] mx-auto" />
                    <p className="mt-3 font-heading font-semibold text-slate-900">No departments yet</p>
                    <p className="text-slate-500 text-sm mt-1">Create departments from the Control Panel</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {depts.map((d) => {
                        const count = countByDept(d.id);
                        const homeMgrs = homeManagersByDept(d.id);
                        const addMgrs = additionalManagersByDept(d.id);
                        return (
                            <div key={d.id} className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 hover:shadow-md transition-all flex flex-col" data-testid={`dept-card-${d.id}`}>
                                <div className="flex items-start justify-between">
                                    <div className="h-10 w-10 rounded-lg bg-teal-50 text-[#25908A] flex items-center justify-center border border-teal-100">
                                        <Building2 className="h-4 w-4" />
                                    </div>
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-blue-100 text-blue-800 border-blue-200">
                                        <Users className="h-3 w-3" />
                                        {count} member{count!==1?"s":""}
                                    </span>
                                </div>
                                <h3 className="mt-4 font-heading text-xl font-semibold text-slate-900">{d.name}</h3>
                                {d.description && <p className="text-sm text-slate-500 mt-1">{d.description}</p>}
                                <div className="mt-4 pt-4 border-t border-slate-100 flex-1">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Managers</div>
                                        {isSuperAdmin && (
                                            <button
                                                onClick={() => setEditing(d)}
                                                data-testid={`dept-edit-managers-${d.id}`}
                                                className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-semibold text-[#25908A] hover:bg-teal-50 transition-colors"
                                            >
                                                <UserPlus className="h-3 w-3" /> Manage
                                            </button>
                                        )}
                                    </div>
                                    {homeMgrs.length === 0 && addMgrs.length === 0 ? (
                                        <div className="text-xs text-slate-400 italic">No managers assigned</div>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {homeMgrs.map((m) => (
                                                <span
                                                    key={m.user_id}
                                                    title={`Home dept manager — ${m.email}`}
                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-teal-50 text-[#1F7F79] border border-teal-200 font-medium"
                                                >
                                                    {m.name}
                                                </span>
                                            ))}
                                            {addMgrs.map((m) => (
                                                <span
                                                    key={m.user_id}
                                                    title={`Additional manager (home: ${(depts.find((x) => x.id === m.department_id) || {}).name || "—"})`}
                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 font-medium"
                                                    data-testid={`dept-additional-mgr-${d.id}-${m.user_id}`}
                                                >
                                                    {m.name}
                                                    <span className="text-[9px] uppercase tracking-wider text-indigo-500 font-bold">+dept</span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {editing && (
                <ManageDepartmentManagersDialog
                    dept={editing}
                    allUsers={users}
                    onClose={() => setEditing(null)}
                    onSaved={async () => {
                        await refreshUsers();
                        setEditing(null);
                        toast.success("Managers updated");
                    }}
                />
            )}
        </div>
    );
};

// ---------- ManageDepartmentManagersDialog ----------
// Super-admin-only picker to grow/shrink a department's *additional*
// manager roster. Home-dept managers (users whose home `department_id` is
// this dept and role=manager) are shown as read-only chips — those
// relationships are edited from the user's profile, not here.
const ManageDepartmentManagersDialog = ({ dept, allUsers, onClose, onSaved }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(new Set());
    const [homeMgrIds, setHomeMgrIds] = useState(new Set());

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get(`/departments/${dept.id}/managers`);
                if (!alive) return;
                const items = r.data?.items || [];
                setSelected(new Set(items.filter((x) => x.is_additional).map((x) => x.user_id)));
                setHomeMgrIds(new Set(items.filter((x) => x.is_home_manager).map((x) => x.user_id)));
            } catch (e) {
                if (alive) toast.error(e?.response?.data?.detail || "Couldn't load managers");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [dept.id]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const candidates = useMemo(() => {
        // Anyone active + not a home manager of THIS dept is a candidate.
        const q = query.trim().toLowerCase();
        return allUsers
            .filter((u) => u.active !== false)
            .filter((u) => !(u.department_id === dept.id && u.role === "manager"))
            .filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [allUsers, query, dept.id]);

    const toggle = (uid) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(uid)) next.delete(uid); else next.add(uid);
            return next;
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/departments/${dept.id}/managers`, { manager_ids: Array.from(selected) });
            await onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-testid="dept-managers-dialog">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl ring-1 ring-slate-900/5 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#1F7F79]">Manage managers</div>
                        <div className="mt-1 font-heading font-semibold text-slate-900 truncate">{dept.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">Add teammates who should also manage this department. Their home-department role stays the same.</div>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-900 -mr-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-5 pt-3 pb-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by name or email…"
                            data-testid="dept-mgr-search"
                            className="w-full h-10 pl-9 pr-3 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none"
                        />
                    </div>
                </div>

                <div className="px-5 pb-3 max-h-[360px] overflow-y-auto">
                    {loading ? (
                        <div className="space-y-2 py-2">
                            {[0,1,2].map((i) => <div key={i} className="h-12 rounded-lg bg-slate-100 animate-pulse" />)}
                        </div>
                    ) : candidates.length === 0 ? (
                        <div className="text-center py-8 text-sm text-slate-500">No users found.</div>
                    ) : (
                        <ul className="space-y-1 py-1">
                            {candidates.map((u) => {
                                const on = selected.has(u.user_id);
                                const isSelf = homeMgrIds.has(u.user_id);
                                const initial = (u.name || u.email || "?").charAt(0).toUpperCase();
                                return (
                                    <li key={u.user_id}>
                                        <button
                                            onClick={() => toggle(u.user_id)}
                                            disabled={isSelf}
                                            data-testid={`dept-mgr-toggle-${u.user_id}`}
                                            className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition-colors ${
                                                on ? "bg-teal-50 ring-1 ring-[#2EB4AC]/40" : "hover:bg-slate-50"
                                            } ${isSelf ? "opacity-60 cursor-not-allowed" : ""}`}
                                        >
                                            <span className="h-8 w-8 rounded-full bg-teal-50 text-[#25908A] border border-teal-100 inline-flex items-center justify-center text-xs font-bold overflow-hidden flex-shrink-0">
                                                {u.picture ? <img src={u.picture} alt="" className="h-full w-full object-cover" /> : initial}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-semibold text-slate-900 truncate">{u.name}</div>
                                                <div className="text-[11px] text-slate-500 truncate">{u.email} · {u.role}</div>
                                            </div>
                                            {isSelf ? (
                                                <span className="text-[10px] font-semibold uppercase tracking-wider text-teal-700 bg-teal-100 px-2 py-0.5 rounded-full">Home mgr</span>
                                            ) : (
                                                <span className={`h-5 w-5 inline-flex items-center justify-center rounded-md border ${on ? "bg-[#2EB4AC] border-[#2EB4AC] text-white" : "border-slate-300"}`}>
                                                    {on && <Check className="h-3.5 w-3.5" />}
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
                    <div className="text-xs text-slate-500">
                        <span className="font-semibold text-slate-700">{selected.size}</span> additional manager{selected.size === 1 ? "" : "s"}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="h-9 px-3 text-sm font-medium text-slate-600 hover:text-slate-900">Cancel</button>
                        <button
                            onClick={save}
                            disabled={saving || loading}
                            data-testid="dept-mgr-save"
                            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-[#2EB4AC] hover:bg-[#25908A] text-white text-sm font-semibold disabled:opacity-50 transition-colors"
                        >
                            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default Departments;
